import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Building2, LayoutGrid, TrendingUp, Settings2,
    FileText, Lock, Unlock, Download, RefreshCw,
    ChevronRight, ChevronDown, X, Eye, AlertTriangle, CheckCircle2,
    Clock, BarChart3, Users, Loader2, ArrowRight,
    Zap, Repeat2, GitBranch, Activity, Search,
    CircleDot, Shield, Brain, Lightbulb,
    Send, ExternalLink, Copy, Check, XCircle, MessageSquare, Trash2
} from 'lucide-react';
import { API_BASE_URL, lockDepartmentAnalysis, unlockDepartmentAnalysis } from '../services/api';
import ReportViewer from './ReportViewer';
import html2pdf from 'html2pdf.js';

const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    const swaPrincipal = localStorage.getItem('swaPrincipal');
    return {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        ...(swaPrincipal ? { 'X-MS-CLIENT-PRINCIPAL': swaPrincipal } : {}),
    };
};

const apiFetch = async (path, opts = {}) => {
    const res = await fetch(`${API_BASE_URL}/api/admin${path}`, {
        headers: getAuthHeaders(),
        ...opts,
    });
    if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const detail = body.detail || body.message;
        const message = typeof detail === 'string' ? detail : detail?.message || detail?.error;
        throw new Error(message || `Request failed (${res.status})`);
    }
    return res.json();
};

/* ------------------------------------------------------------------ */
/*  Shared small helpers                                               */
/* ------------------------------------------------------------------ */

function StatusBadge({ completed, total }) {
    if (total === 0) return <span className="ds-badge ds-badge-neutral"><span className="ds-badge-dot" />Empty</span>;
    if (completed === total) return <span className="ds-badge ds-badge-success"><span className="ds-badge-dot" />Ready</span>;
    if (completed === 0) return <span className="ds-badge ds-badge-warning"><span className="ds-badge-dot" />Pending</span>;
    return <span className="ds-badge ds-badge-warning"><span className="ds-badge-dot" />In Progress</span>;
}

function Spinner({ text }) {
    return (
        <div className="ds-loading">
            <Loader2 className="ds-spinner" size={20} />
            {text && <span style={{ marginLeft: 8 }}>{text}</span>}
        </div>
    );
}

function EmptyState({ icon: Icon, title, desc }) {
    return (
        <div className="ds-empty-state">
            <Icon className="ds-empty-state-icon" size={48} />
            <div className="ds-empty-state-title">{title}</div>
            {desc && <div className="ds-empty-state-desc">{desc}</div>}
        </div>
    );
}

function GenerationLockedNotice({ status }) {
    if (!status?.locked) return null;

    return (
        <div className="ds-badge ds-badge-warning" style={{ display: 'inline-flex', marginBottom: 12 }}>
            <Lock size={12} /> {status.message || 'Report generation is locked.'}
        </div>
    );
}

/* ------------------------------------------------------------------ */
/*  TAB 1 — Organization                                              */
/* ------------------------------------------------------------------ */

function OrgTab({ stats, overview, onSelectDept }) {
    const [slideOpen, setSlideOpen] = useState(false);
    const [slideDept, setSlideDept] = useState(null);

    const departments = overview?.departments || [];
    const lockedCount = departments.filter(d => d.surveys?.some(s => s.has_report)).length; // approximation

    const openDeptSlide = (dept) => {
        setSlideDept(dept);
        setSlideOpen(true);
    };

    return (
        <>
            {/* Stat cards */}
            <div className="ds-stats-grid">
                <div className="ds-stat-card">
                    <div className="ds-stat-header">
                        <span className="ds-stat-label">Completion Rate</span>
                        <span className="ds-stat-icon blue"><CheckCircle2 size={16} /></span>
                    </div>
                    <div className="ds-stat-value">{stats?.completion_rate ?? 0}%</div>
                </div>
                <div className="ds-stat-card">
                    <div className="ds-stat-header">
                        <span className="ds-stat-label">Reports Generated</span>
                        <span className="ds-stat-icon green"><FileText size={16} /></span>
                    </div>
                    <div className="ds-stat-value">{stats?.total_reports ?? 0}</div>
                </div>
                <div className="ds-stat-card">
                    <div className="ds-stat-header">
                        <span className="ds-stat-label">Departments</span>
                        <span className="ds-stat-icon orange"><Building2 size={16} /></span>
                    </div>
                    <div className="ds-stat-value">{stats?.departments?.length ?? departments.length}</div>
                </div>
                <div className="ds-stat-card">
                    <div className="ds-stat-header">
                        <span className="ds-stat-label">Total Surveys</span>
                        <span className="ds-stat-icon purple"><Users size={16} /></span>
                    </div>
                    <div className="ds-stat-value">{stats?.total_sessions ?? 0}</div>
                </div>
            </div>

            {/* Department card grid */}
            <div className="ds-card" style={{ marginTop: 16 }}>
                <div className="ds-card-header">
                    <div className="ds-card-header-left">
                        <LayoutGrid className="ds-card-icon" size={16} />
                        <span className="ds-card-title">Departments</span>
                    </div>
                </div>
                <div className="ds-card-body">
                    {departments.length === 0 ? (
                        <EmptyState icon={Building2} title="No departments yet" desc="Surveys will appear here once employees begin." />
                    ) : (
                        <div className="ds-dept-grid">
                            {departments.map(dept => {
                                const pct = dept.total > 0 ? Math.round((dept.completed / dept.total) * 100) : 0;
                                return (
                                    <button key={dept.name} className="ds-dept-card" onClick={() => openDeptSlide(dept)} style={{ cursor: 'pointer', textAlign: 'left', width: '100%' }}>
                                        <div className="ds-dept-card-header">
                                            <span className="ds-dept-card-name">{dept.name}</span>
                                            <StatusBadge completed={dept.completed} total={dept.total} />
                                        </div>
                                        <div className="ds-dept-card-stats">
                                            <div className="ds-dept-stat">
                                                <span className="ds-dept-stat-value">{dept.completed}</span>
                                                <span className="ds-dept-stat-label">Completed</span>
                                            </div>
                                            <div className="ds-dept-stat">
                                                <span className="ds-dept-stat-value">{dept.total}</span>
                                                <span className="ds-dept-stat-label">Total</span>
                                            </div>
                                            <div className="ds-dept-stat">
                                                <span className="ds-dept-stat-value">{dept.reports_generated}</span>
                                                <span className="ds-dept-stat-label">Reports</span>
                                            </div>
                                        </div>
                                        <div className="ds-progress">
                                            <div className="ds-progress-fill blue" style={{ width: `${pct}%` }} />
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {/* Slide-over */}
            {slideOpen && slideDept && (
                <>
                    <div className={`ds-slide-over-backdrop${slideOpen ? ' open' : ''}`} onClick={() => setSlideOpen(false)} />
                    <div className={`ds-slide-over${slideOpen ? ' open' : ''}`}>
                        <div className="ds-slide-over-header">
                            <span className="ds-slide-over-title">{slideDept.name}</span>
                            <button className="ds-slide-over-close" onClick={() => setSlideOpen(false)}><X size={18} /></button>
                        </div>
                        <div className="ds-slide-over-body">
                            <div className="ds-stats-grid" style={{ marginBottom: 16 }}>
                                <div className="ds-stat-card">
                                    <div className="ds-stat-header"><span className="ds-stat-label">Completed</span></div>
                                    <div className="ds-stat-value">{slideDept.completed} / {slideDept.total}</div>
                                </div>
                                <div className="ds-stat-card">
                                    <div className="ds-stat-header"><span className="ds-stat-label">Reports</span></div>
                                    <div className="ds-stat-value">{slideDept.reports_generated}</div>
                                </div>
                            </div>
                            <div className="ds-card">
                                <div className="ds-card-header">
                                    <div className="ds-card-header-left">
                                        <Users className="ds-card-icon" size={16} />
                                        <span className="ds-card-title">Employee Surveys</span>
                                    </div>
                                </div>
                                <div className="ds-card-body">
                                    {(slideDept.surveys || []).length === 0 ? (
                                        <EmptyState icon={Users} title="No surveys" />
                                    ) : (
                                        <table className="ds-table">
                                            <thead>
                                                <tr>
                                                    <th>Employee</th>
                                                    <th>Role</th>
                                                    <th>Status</th>
                                                    <th>Report</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {slideDept.surveys.map(s => (
                                                    <tr key={s.session_id}>
                                                        <td>{s.employee_name}</td>
                                                        <td>{s.role_title}</td>
                                                        <td>
                                                            <span className={`ds-badge ${s.status === 'completed' ? 'ds-badge-success' : s.status === 'in_progress' ? 'ds-badge-warning' : 'ds-badge-neutral'}`}>
                                                                <span className="ds-badge-dot" />{s.status}
                                                            </span>
                                                        </td>
                                                        <td>{s.has_report ? <CheckCircle2 size={14} style={{ color: 'var(--color-success)' }} /> : <Clock size={14} style={{ color: 'var(--color-text-quaternary)' }} />}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                            </div>
                            <div style={{ marginTop: 16 }}>
                                <button className="ds-btn ds-btn-primary" onClick={() => { setSlideOpen(false); onSelectDept(slideDept.name); }}>
                                    <BarChart3 size={14} /> View Department Analysis <ChevronRight size={14} />
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </>
    );
}

/* ------------------------------------------------------------------ */
/*  TAB 2 — Departments                                                */
/* ------------------------------------------------------------------ */

function DeptTab({ departments, initialDept, onDeptUsed, reportGenerationLock }) {
    const [selectedDept, setSelectedDept] = useState('');
    const [trends, setTrends] = useState(null);
    const [aiAnalysis, setAiAnalysis] = useState(null);
    const [aiStatus, setAiStatus] = useState(null); // 'idle' | 'running' | 'completed' | 'error'
    const [loading, setLoading] = useState(false);
    const [aiLoading, setAiLoading] = useState(false);
    const [lockBusy, setLockBusy] = useState(false);
    const [error, setError] = useState(null);
    const pollRef = useRef(null);
    const pdfRef = useRef(null);

    const clearPoll = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
    const generationLocked = Boolean(reportGenerationLock?.locked);
    const generationLockMessage = reportGenerationLock?.message || 'Report generation is locked.';

    useEffect(() => () => clearPoll(), []);

    // Auto-select department when navigated from OrgTab
    useEffect(() => {
        if (initialDept && initialDept !== selectedDept) {
            handleSelect(initialDept);
            if (onDeptUsed) onDeptUsed();
        }
    }, [initialDept]); // eslint-disable-line

    const fetchTrends = useCallback(async (dept) => {
        setLoading(true);
        setError(null);
        setAiAnalysis(null);
        setAiStatus('idle');
        clearPoll();
        try {
            const data = await apiFetch(`/trends/department/${encodeURIComponent(dept)}`);
            setTrends(data);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, []);

    const startAiAnalysis = useCallback(async (dept) => {
        if (generationLocked) {
            setAiStatus('error');
            setError(generationLockMessage);
            return;
        }

        setAiLoading(true);
        setAiStatus('running');
        clearPoll();
        try {
            const res = await apiFetch(`/trends/department/${encodeURIComponent(dept)}/ai-analysis/start`, { method: 'POST' });
            if (res.status === 'completed') {
                setAiAnalysis(res.result?.analysis || res.result);
                setAiStatus('completed');
                setAiLoading(false);
                return;
            }
            // Poll for completion
            pollRef.current = setInterval(async () => {
                try {
                    const poll = await apiFetch(`/trends/department/${encodeURIComponent(dept)}/ai-analysis/status`);
                    if (poll.status === 'completed') {
                        clearPoll();
                        setAiAnalysis(poll.result?.analysis || poll.result);
                        setAiStatus('completed');
                        setAiLoading(false);
                    } else if (poll.status === 'error') {
                        clearPoll();
                        setAiStatus('error');
                        setError(poll.error || 'AI analysis failed');
                        setAiLoading(false);
                    }
                } catch {
                    clearPoll();
                    setAiStatus('error');
                    setAiLoading(false);
                }
            }, 3000);
        } catch (e) {
            setAiStatus('error');
            setError(e.message);
            setAiLoading(false);
        }
    }, [generationLocked, generationLockMessage]);

    const handleSelect = (dept) => {
        setSelectedDept(dept);
        if (dept) {
            fetchTrends(dept);
            startAiAnalysis(dept);
        } else {
            setTrends(null);
            setAiAnalysis(null);
        }
    };

    const toggleLock = async () => {
        if (!trends || lockBusy) return;
        setLockBusy(true);
        try {
            const isLocked = trends.lock_info?.locked;
            if (isLocked) {
                await unlockDepartmentAnalysis(selectedDept);
            } else {
                await lockDepartmentAnalysis(selectedDept, 'Locked from Reports Hub');
            }
            await fetchTrends(selectedDept);
        } catch (e) {
            setError(e.message);
        } finally {
            setLockBusy(false);
        }
    };

    const exportPdf = () => {
        const el = pdfRef.current;
        if (!el) return;
        html2pdf().set({
            margin: [10, 10],
            filename: `${selectedDept}-department-analysis.pdf`,
            html2canvas: { scale: 2 },
            jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        }).from(el).save();
    };

    const isLocked = trends?.lock_info?.locked;

    return (
        <>
            <div className="ds-toolbar" style={{ marginBottom: 16 }}>
                <select className="ds-filter-select" value={selectedDept} onChange={e => handleSelect(e.target.value)}>
                    <option value="">Select a department...</option>
                    {departments.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
            </div>

            {!selectedDept && <EmptyState icon={Search} title="Select a department" desc="Choose a department above to view its analysis." />}
            {loading && <Spinner text="Loading department data..." />}
            {error && !loading && <div className="ds-badge ds-badge-error" style={{ display: 'inline-flex', marginBottom: 12 }}><AlertTriangle size={14} />{error}</div>}
            <GenerationLockedNotice status={reportGenerationLock} />

            {trends && !loading && (
                <div ref={pdfRef}>
                    {/* Header actions */}
                    <div className="ds-action-row" style={{ marginBottom: 16 }}>
                        <button className={`ds-btn ${isLocked ? 'ds-btn-secondary' : 'ds-btn-primary'}`} onClick={toggleLock} disabled={lockBusy}>
                            {lockBusy ? <Loader2 size={14} className="ds-spinner" /> : isLocked ? <><Unlock size={14} /> Unlock</> : <><Lock size={14} /> Lock</>}
                        </button>
                        <button className="ds-btn ds-btn-secondary" onClick={exportPdf}>
                            <Download size={14} /> Export PDF
                        </button>
                    </div>

                    {isLocked && (
                        <div className="ds-badge ds-badge-warning" style={{ display: 'inline-flex', marginBottom: 12 }}>
                            <Lock size={12} /> Locked by {trends.lock_info.locked_by} {trends.lock_info.locked_at ? `on ${new Date(trends.lock_info.locked_at).toLocaleDateString()}` : ''}
                        </div>
                    )}

                    {/* Summary stats */}
                    <div className="ds-stats-grid" style={{ marginBottom: 16 }}>
                        <div className="ds-stat-card">
                            <div className="ds-stat-header"><span className="ds-stat-label">Surveys</span><span className="ds-stat-icon blue"><Users size={16} /></span></div>
                            <div className="ds-stat-value">{trends.total_surveys}</div>
                        </div>
                        <div className="ds-stat-card">
                            <div className="ds-stat-header"><span className="ds-stat-label">Automation Opps</span><span className="ds-stat-icon green"><Zap size={16} /></span></div>
                            <div className="ds-stat-value">{trends.summary?.total_automation_opportunities ?? 0}</div>
                        </div>
                        <div className="ds-stat-card">
                            <div className="ds-stat-header"><span className="ds-stat-label">Redundancies</span><span className="ds-stat-icon orange"><Repeat2 size={16} /></span></div>
                            <div className="ds-stat-value">{trends.summary?.total_redundancies ?? 0}</div>
                        </div>
                        <div className="ds-stat-card">
                            <div className="ds-stat-header"><span className="ds-stat-label">Bottlenecks</span><span className="ds-stat-icon purple"><AlertTriangle size={16} /></span></div>
                            <div className="ds-stat-value">{trends.summary?.total_bottlenecks ?? 0}</div>
                        </div>
                    </div>

                    {/* Time Allocation */}
                    {trends.summary?.avg_time_allocation?.length > 0 && (() => {
                        const rawTa = trends.summary.avg_time_allocation;
                        const deptColors = ['#007AFF', '#5856D6', '#34C759', '#FF9500', '#FF3B30', '#AF52DE', '#5AC8FA', '#FF6482', '#30D5C8', '#FFD60A'];
                        const totalEmployees = trends.total_surveys || 0;
                        // Re-normalize: take top 10, compute their share, add "Other" for remainder
                        const displayItems = rawTa.slice(0, 10);
                        const displaySum = displayItems.reduce((s, item) => s + item.average_percentage, 0);
                        const totalAll = rawTa.reduce((s, item) => s + item.average_percentage, 0);
                        const otherPct = totalAll > displaySum ? totalAll - displaySum : 0;
                        const normFactor = totalAll > 0 ? 100 / totalAll : 1;
                        const ta = displayItems.map(item => ({
                            ...item,
                            displayPct: Math.round(item.average_percentage * normFactor * 10) / 10
                        }));
                        const otherDisplayPct = Math.round(otherPct * normFactor * 10) / 10;
                        return (
                        <div className="ds-report-section">
                            <div className="ds-report-section-header">
                                <Clock className="ds-section-icon" size={16} />
                                <span className="ds-section-title">How This Department Spends Their Time</span>
                                {totalEmployees > 0 && <span style={{ fontSize: '0.75rem', color: '#86868B', marginLeft: 8 }}>{totalEmployees} employee{totalEmployees !== 1 ? 's' : ''} surveyed</span>}
                            </div>
                            <div className="ds-section-body" style={{ padding: 'var(--space-5)' }}>
                                <p style={{ fontSize: '0.8rem', color: '#86868B', marginBottom: 14, lineHeight: 1.5 }}>
                                    Aggregated from employee self-reported time breakdowns. Each bar segment shows relative time spent on that activity across the department.
                                    {totalEmployees > 1 ? ' Numbers in parentheses show how many employees reported that activity.' : ''}
                                </p>
                                <div className="ds-time-stacked-bar" style={{ marginBottom: 16, height: 14 }}>
                                    {ta.slice(0, 8).map((item, i) => (
                                        <div
                                            key={i}
                                            className="ds-time-segment"
                                            style={{
                                                width: `${Math.max(item.displayPct, 2)}%`,
                                                background: deptColors[i % deptColors.length],
                                                opacity: 1 - (i * 0.07)
                                            }}
                                            title={`${item.category}: ${item.displayPct}%`}
                                        />
                                    ))}
                                    {otherDisplayPct > 1 && (
                                        <div
                                            className="ds-time-segment"
                                            style={{ width: `${otherDisplayPct}%`, background: '#D1D1D6' }}
                                            title={`Other activities: ${otherDisplayPct}%`}
                                        />
                                    )}
                                </div>
                                <div className="ds-time-legend">
                                    {ta.map((item, i) => (
                                        <div className="ds-time-legend-item" key={i}>
                                            <span className="ds-time-legend-dot" style={{ background: deptColors[i % deptColors.length], opacity: i < 8 ? 1 - (i * 0.07) : 0.5 }} />
                                            <span className="ds-time-legend-label">{item.category}{item.reported_by_count > 1 ? ` (${item.reported_by_count})` : ''}</span>
                                            <span className="ds-time-legend-pct">{item.displayPct}%</span>
                                        </div>
                                    ))}
                                    {otherDisplayPct > 1 && (
                                        <div className="ds-time-legend-item">
                                            <span className="ds-time-legend-dot" style={{ background: '#D1D1D6' }} />
                                            <span className="ds-time-legend-label" style={{ fontStyle: 'italic' }}>Other activities ({rawTa.length - 10}+ categories)</span>
                                            <span className="ds-time-legend-pct">{otherDisplayPct}%</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        );
                    })()}

                    {/* Automation Opportunities */}
                    {trends.automation_opportunities?.length > 0 && (
                        <div className="ds-report-section">
                            <div className="ds-report-section-header">
                                <Zap className="ds-section-icon" size={16} />
                                <span className="ds-section-title">Top Automation Opportunities</span>
                            </div>
                            <div className="ds-section-body">
                                {trends.automation_opportunities.slice(0, 10).map((opp, i) => {
                                    const sev = opp.priorities?.includes('high') ? 'high' : opp.priorities?.includes('medium') ? 'medium' : 'low';
                                    return (
                                        <div className="ds-finding" key={i}>
                                            <div className={`ds-finding-severity ${sev}`} />
                                            <div className="ds-finding-content">
                                                <div className="ds-finding-title">{opp.task}</div>
                                                <div className="ds-finding-desc">{opp.potential_solutions?.[0] || ''}</div>
                                                <div className="ds-finding-meta">
                                                    {opp.count} employee{opp.count !== 1 ? 's' : ''} mentioning
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Redundancy Patterns */}
                    {trends.redundancy_patterns?.length > 0 && (
                        <div className="ds-report-section">
                            <div className="ds-report-section-header">
                                <Repeat2 className="ds-section-icon" size={16} />
                                <span className="ds-section-title">Cross-Employee Redundancies</span>
                            </div>
                            <div className="ds-section-body">
                                <table className="ds-table">
                                    <thead><tr><th>Task</th><th>Employees</th><th>Combined Time</th></tr></thead>
                                    <tbody>
                                        {trends.redundancy_patterns.slice(0, 10).map((rp, i) => (
                                            <tr key={i}>
                                                <td>{rp.task}</td>
                                                <td>{rp.employees_involved?.join(', ')}</td>
                                                <td>{rp.combined_time_percentage}%</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* AI Analysis section */}
                    <div className="ds-report-section" style={{ marginTop: 16 }}>
                        <div className="ds-report-section-header">
                            <Brain className="ds-section-icon" size={16} />
                            <span className="ds-section-title">AI Department Analysis</span>
                        </div>
                        <div className="ds-section-body">
                            {aiLoading && <Spinner text="AI analysis running..." />}
                            {aiStatus === 'error' && <div style={{ color: 'var(--color-error)' }}>Analysis failed. Try again.</div>}
                            {aiStatus === 'idle' && !aiLoading && (
                                <button className="ds-btn ds-btn-primary" onClick={() => startAiAnalysis(selectedDept)} disabled={generationLocked}>
                                    <Brain size={14} /> Generate AI Analysis
                                </button>
                            )}
                            {aiAnalysis && aiStatus === 'completed' && (
                                <AiAnalysisDisplay analysis={aiAnalysis} />
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

function AiAnalysisDisplay({ analysis }) {
    if (!analysis) return null;

    /* ------ Renderers for known structured types ------ */

    const renderString = (val) => (
        <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{val}</div>
    );

    const renderPainPoints = (items) => {
        if (!Array.isArray(items)) return renderObject(items);
        return items.map((pp, i) => {
            const issue = pp.issue || pp.title || pp.pain_point || '';
            const impact = pp.impact || pp.description || '';
            const solution = pp.potential_solution || pp.recommendation || '';
            const count = pp.employees_mentioning || pp.count || 0;
            return (
                <div className="ds-finding" key={i}>
                    <div className="ds-finding-severity high" />
                    <div className="ds-finding-content">
                        <div className="ds-finding-title">{issue}</div>
                        {impact && <div className="ds-finding-desc">{impact}</div>}
                        {solution && <div className="ds-finding-desc" style={{ marginTop: 4, fontStyle: 'italic' }}>Recommendation: {solution}</div>}
                        {count > 0 && <div className="ds-finding-meta">{count} employee{count !== 1 ? 's' : ''} affected</div>}
                    </div>
                </div>
            );
        });
    };

    const renderKeyThemes = (items) => {
        if (!Array.isArray(items)) {
            if (typeof items === 'string') return renderString(items);
            return renderObject(items);
        }
        return items.map((item, i) => {
            const label = typeof item === 'string' ? item : (item.theme || item.title || item.name || '');
            const desc = typeof item === 'string' ? '' : (item.description || item.details || '');
            return (
                <div className="ds-finding" key={i}>
                    <div className="ds-finding-severity medium" />
                    <div className="ds-finding-content">
                        <div className="ds-finding-title">{label}</div>
                        {desc && <div className="ds-finding-desc">{desc}</div>}
                    </div>
                </div>
            );
        });
    };

    const renderTechPatterns = (val) => {
        if (typeof val === 'string') return renderString(val);
        const toolsMostUsed = val.tools_most_used || val.most_used_tools || [];
        const frustrations = val.common_frustrations || val.frustrations || [];
        const requested = val.commonly_requested || val.requested_tools || [];
        return (
            <div>
                {toolsMostUsed.length > 0 && (
                    <div style={{ marginBottom: 12 }}>
                        <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, marginBottom: 6, color: 'var(--color-text-primary)' }}>Most Used Tools</div>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            {toolsMostUsed.map((t, i) => (
                                <span key={i} className="ds-badge ds-badge-neutral">{typeof t === 'string' ? t : t.name || t.tool}</span>
                            ))}
                        </div>
                    </div>
                )}
                {frustrations.length > 0 && (
                    <div style={{ marginBottom: 12 }}>
                        <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, marginBottom: 6, color: 'var(--color-text-primary)' }}>Common Frustrations</div>
                        {frustrations.map((f, i) => (
                            <div className="ds-finding" key={i}>
                                <div className="ds-finding-severity high" />
                                <div className="ds-finding-content">
                                    <div className="ds-finding-title">{f.tool || f.name || f.title || 'Tool'}</div>
                                    <div className="ds-finding-desc">{f.issues || f.description || f.frustration || ''}</div>
                                    {f.employees_affected && <div className="ds-finding-meta">{f.employees_affected} employee{f.employees_affected !== 1 ? 's' : ''} affected</div>}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                {requested.length > 0 && (
                    <div>
                        <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, marginBottom: 6, color: 'var(--color-text-primary)' }}>Commonly Requested</div>
                        {requested.map((r, i) => (
                            <div className="ds-finding" key={i}>
                                <div className="ds-finding-severity low" />
                                <div className="ds-finding-content">
                                    <div className="ds-finding-title">{r.tool_type || r.name || r.tool || 'Tool'}</div>
                                    <div className="ds-finding-desc">{r.problem_it_solves || r.description || ''}</div>
                                    {r.employees_requesting && <div className="ds-finding-meta">{r.employees_requesting} employee{r.employees_requesting !== 1 ? 's' : ''} requesting</div>}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    const renderTimeAllocation = (val) => {
        if (typeof val === 'string') return renderString(val);
        const highTime = val.high_time_activities || [];
        const observations = val.observations || '';
        return (
            <div>
                {highTime.length > 0 && highTime.map((item, i) => (
                    <div className="ds-bar-row" key={i}>
                        <span className="ds-bar-label" style={{ width: 200 }}>{item.activity}</span>
                        <div className="ds-bar-track">
                            <div className="ds-bar-fill" style={{ width: `${Math.min(item.average_percentage || 0, 100)}%`, background: item.streamline_potential === 'high' ? 'var(--color-error)' : item.streamline_potential === 'medium' ? 'var(--color-warning)' : 'var(--color-primary)' }} />
                        </div>
                        <span className="ds-bar-value">{item.average_percentage}%</span>
                    </div>
                ))}
                {observations && (
                    <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', marginTop: 12, lineHeight: 1.6, fontStyle: 'italic' }}>{observations}</div>
                )}
            </div>
        );
    };

    const renderRedundantWork = (items) => {
        if (!Array.isArray(items)) {
            if (typeof items === 'string') return renderString(items);
            return renderObject(items);
        }
        return (
            <table className="ds-table">
                <thead>
                    <tr><th>Task</th><th>Employees</th><th>Est. Time</th><th>Opportunity</th></tr>
                </thead>
                <tbody>
                    {items.map((rw, i) => (
                        <tr key={i}>
                            <td>{rw.task_description || rw.task || rw.title || ''}</td>
                            <td>{(rw.employees_affected || rw.employees || []).join(', ')}</td>
                            <td>{rw.estimated_combined_time || rw.time || ''}</td>
                            <td style={{ fontSize: 'var(--font-size-xs)' }}>{rw.consolidation_opportunity || rw.opportunity || ''}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    };

    const renderObject = (val) => {
        // Generic fallback: render key-value pairs nicely
        const entries = Object.entries(val).filter(([, v]) => v != null);
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {entries.map(([key, v]) => (
                    <div key={key}>
                        <div style={{ fontSize: 'var(--font-size-xs)', fontWeight: 600, color: 'var(--color-text-tertiary)', textTransform: 'capitalize', marginBottom: 2 }}>
                            {key.replace(/_/g, ' ')}
                        </div>
                        <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
                            {typeof v === 'string' ? v : Array.isArray(v) ? v.map((item, i) => <div key={i}>{typeof item === 'string' ? item : (item.name || item.title || JSON.stringify(item))}</div>) : JSON.stringify(v)}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const sections = [
        { key: 'department_summary', title: 'Department Summary', icon: Building2, render: renderString },
        { key: 'common_pain_points', title: 'Common Pain Points', icon: AlertTriangle, render: renderPainPoints },
        { key: 'key_themes', title: 'Key Themes', icon: Lightbulb, render: renderKeyThemes },
        { key: 'technology_patterns', title: 'Technology Patterns', icon: Activity, render: renderTechPatterns },
        { key: 'time_allocation_patterns', title: 'Time Allocation Patterns', icon: Clock, render: renderTimeAllocation },
        { key: 'redundant_work', title: 'Redundant Work', icon: Repeat2, render: renderRedundantWork },
    ];

    return (
        <div>
            {sections.map(sec => {
                const val = analysis[sec.key];
                if (!val) return null;
                const Icon = sec.icon;
                return (
                    <div key={sec.key} className="ds-report-section" style={{ marginBottom: 16 }}>
                        <div className="ds-report-section-header">
                            <Icon className="ds-section-icon" size={16} />
                            <span className="ds-section-title">{sec.title}</span>
                        </div>
                        <div className="ds-section-body">
                            {typeof val === 'string' ? renderString(val) : sec.render(val)}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

/* ------------------------------------------------------------------ */
/*  TAB 3 — Trends                                                     */
/* ------------------------------------------------------------------ */

function TrendsTab({ trendsAll, deptTrendsMap, departments, fetchDeptTrends }) {
    const [loading, setLoading] = useState(false);
    const fetchedRef = useRef(new Set());

    useEffect(() => {
        if (departments.length > 0 && trendsAll) {
            const toFetch = departments.filter(d => !deptTrendsMap[d] && !fetchedRef.current.has(d));
            if (toFetch.length > 0) {
                toFetch.forEach(d => fetchedRef.current.add(d));
                setLoading(true);
                Promise.all(toFetch.map(d => fetchDeptTrends(d))).finally(() => setLoading(false));
            }
        }
    }, [departments, trendsAll, deptTrendsMap, fetchDeptTrends]);

    if (!trendsAll) return <Spinner text="Loading trends..." />;

    const deptSummaryRaw = trendsAll.department_summary || [];
    // Override raw counts with grouped counts from per-department data when available
    const deptSummary = deptSummaryRaw.map(ds => {
        const deptData = deptTrendsMap[ds.department];
        if (deptData?.summary) {
            // Use grouped automation count (significant only: multi-employee or high-priority)
            const groupedAutomation = deptData.automation_opportunities || [];
            const significantCount = deptData.summary.total_automation_opportunities ?? ds.automation_count;
            const highPriorityCount = groupedAutomation.filter(
                a => a.priorities?.includes('high')
            ).length;
            // For redundancies: use cross-employee if available, otherwise keep self-reported
            const redundancies = (deptData.summary.total_redundancies > 0)
                ? deptData.summary.total_redundancies
                : ds.redundancy_count;
            return {
                ...ds,
                automation_count: significantCount,
                redundancy_count: redundancies,
                high_priority_automation: highPriorityCount
            };
        }
        return ds;
    });

    // Aggregate redundancies across departments
    const allRedundancies = [];
    const allHandoffs = [];
    Object.values(deptTrendsMap).forEach(dt => {
        (dt.redundancy_patterns || []).forEach(rp => {
            allRedundancies.push({ ...rp, department: dt.department });
        });
        (dt.handoff_patterns || []).forEach(hp => {
            allHandoffs.push({ ...hp, department: dt.department });
        });
    });

    return (
        <>
            {/* Org-wide scores */}
            <div className="ds-card" style={{ marginBottom: 16 }}>
                <div className="ds-card-header">
                    <div className="ds-card-header-left">
                        <Shield className="ds-card-icon" size={16} />
                        <span className="ds-card-title">Organization Overview</span>
                    </div>
                </div>
                <div className="ds-card-body">
                    <div className="ds-stats-grid">
                        <div className="ds-stat-card">
                            <div className="ds-stat-header"><span className="ds-stat-label">Total Reports</span><span className="ds-stat-icon blue"><FileText size={16} /></span></div>
                            <div className="ds-stat-value">{trendsAll.total_reports}</div>
                        </div>
                        <div className="ds-stat-card">
                            <div className="ds-stat-header"><span className="ds-stat-label">Departments Analyzed</span><span className="ds-stat-icon green"><Building2 size={16} /></span></div>
                            <div className="ds-stat-value">{trendsAll.departments_analyzed}</div>
                        </div>
                        <div className="ds-stat-card">
                            <div className="ds-stat-header"><span className="ds-stat-label">Automation Opportunities</span><span className="ds-stat-icon orange"><Zap size={16} /></span></div>
                            <div className="ds-stat-value">{trendsAll.total_automation_opportunities}</div>
                        </div>
                        <div className="ds-stat-card">
                            <div className="ds-stat-header"><span className="ds-stat-label">High Priority Items</span><span className="ds-stat-icon purple"><AlertTriangle size={16} /></span></div>
                            <div className="ds-stat-value">{trendsAll.high_priority_items}</div>
                        </div>
                    </div>

                    {/* Health categorical grid */}
                    <div className="ds-health-grid" style={{ marginTop: 16 }}>
                        <div className="ds-health-item">
                            <div className="ds-health-label">Automation Readiness</div>
                            <div className={`ds-health-value ${trendsAll.total_automation_opportunities > 20 ? 'good' : trendsAll.total_automation_opportunities > 5 ? 'moderate' : 'poor'}`}>
                                {trendsAll.total_automation_opportunities > 20 ? 'Strong' : trendsAll.total_automation_opportunities > 5 ? 'Adequate' : 'Needs Attention'}
                            </div>
                        </div>
                        <div className="ds-health-item">
                            <div className="ds-health-label">Process Efficiency</div>
                            <div className={`ds-health-value ${trendsAll.total_redundancies < 5 ? 'good' : trendsAll.total_redundancies < 15 ? 'moderate' : 'poor'}`}>
                                {trendsAll.total_redundancies < 5 ? 'Strong' : trendsAll.total_redundancies < 15 ? 'Adequate' : 'Needs Attention'}
                            </div>
                        </div>
                        <div className="ds-health-item">
                            <div className="ds-health-label">Department Coverage</div>
                            <div className={`ds-health-value ${trendsAll.departments_analyzed >= departments.length ? 'good' : trendsAll.departments_analyzed > 0 ? 'moderate' : 'poor'}`}>
                                {trendsAll.departments_analyzed >= departments.length ? 'Strong' : trendsAll.departments_analyzed > 0 ? 'Adequate' : 'Needs Attention'}
                            </div>
                        </div>
                        <div className="ds-health-item">
                            <div className="ds-health-label">Data Completeness</div>
                            <div className={`ds-health-value ${trendsAll.total_reports >= 10 ? 'good' : trendsAll.total_reports >= 3 ? 'moderate' : 'poor'}`}>
                                {trendsAll.total_reports >= 10 ? 'Strong' : trendsAll.total_reports >= 3 ? 'Adequate' : 'Needs Attention'}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Department Comparison Table */}
            <div className="ds-card" style={{ marginBottom: 16 }}>
                <div className="ds-card-header">
                    <div className="ds-card-header-left">
                        <BarChart3 className="ds-card-icon" size={16} />
                        <span className="ds-card-title">Department Comparison</span>
                    </div>
                </div>
                <div className="ds-card-body">
                    {deptSummary.length === 0 ? (
                        <EmptyState icon={BarChart3} title="No department data yet" />
                    ) : (
                        <table className="ds-table">
                            <thead>
                                <tr>
                                    <th>Department</th>
                                    <th>Surveys</th>
                                    <th>Automation Opps</th>
                                    <th>Redundancies</th>
                                    <th>High Priority</th>
                                </tr>
                            </thead>
                            <tbody>
                                {deptSummary.map((ds, i) => (
                                    <tr key={i}>
                                        <td style={{ fontWeight: 600 }}>{ds.department}</td>
                                        <td>{ds.completed_surveys}</td>
                                        <td>{ds.automation_count}</td>
                                        <td>{ds.redundancy_count}</td>
                                        <td>{ds.high_priority_automation}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* Average Time Allocation per department */}
            {loading && <Spinner text="Loading department details..." />}
            {Object.keys(deptTrendsMap).length > 0 && (
                <div className="ds-card" style={{ marginBottom: 16 }}>
                    <div className="ds-card-header">
                        <div className="ds-card-header-left">
                            <Clock className="ds-card-icon" size={16} />
                            <span className="ds-card-title">Time Allocation by Department</span>
                        </div>
                    </div>
                    <div className="ds-card-body">
                        <div className="ds-time-alloc-grid">
                            {Object.entries(deptTrendsMap).map(([dept, dt]) => {
                                const rawTa = dt.summary?.avg_time_allocation;
                                if (!rawTa || rawTa.length === 0) return null;
                                const totalEmployees = dt.total_surveys || dt.reports_generated || 0;
                                const deptColors = ['#007AFF', '#5856D6', '#34C759', '#FF9500', '#FF3B30', '#AF52DE', '#5AC8FA', '#FF6482'];
                                // Re-normalize top 5 to proportional display
                                const topItems = rawTa.slice(0, 5);
                                const totalAll = rawTa.reduce((s, item) => s + item.average_percentage, 0);
                                const normFactor = totalAll > 0 ? 100 / totalAll : 1;
                                const ta = topItems.map(item => ({
                                    ...item,
                                    displayPct: Math.round(item.average_percentage * normFactor * 10) / 10
                                }));
                                const otherPct = Math.round((totalAll - topItems.reduce((s, item) => s + item.average_percentage, 0)) * normFactor * 10) / 10;
                                return (
                                    <div className="ds-time-dept-card" key={dept}>
                                        <div className="ds-time-dept-header">
                                            <span className="ds-time-dept-name">{dept}</span>
                                            {totalEmployees > 0 && <span className="ds-time-dept-count">{totalEmployees} employee{totalEmployees !== 1 ? 's' : ''}</span>}
                                        </div>
                                        <div className="ds-time-stacked-bar">
                                            {ta.map((item, i) => (
                                                <div
                                                    key={i}
                                                    className="ds-time-segment"
                                                    style={{
                                                        width: `${Math.max(item.displayPct, 2)}%`,
                                                        background: deptColors[i % deptColors.length],
                                                        opacity: 1 - (i * 0.1)
                                                    }}
                                                    title={`${item.category}: ${item.displayPct}%`}
                                                />
                                            ))}
                                            {otherPct > 1 && (
                                                <div className="ds-time-segment" style={{ width: `${otherPct}%`, background: '#D1D1D6' }} title={`Other: ${otherPct}%`} />
                                            )}
                                        </div>
                                        <div className="ds-time-legend">
                                            {ta.map((item, i) => (
                                                <div className="ds-time-legend-item" key={i}>
                                                    <span className="ds-time-legend-dot" style={{ background: deptColors[i % deptColors.length], opacity: 1 - (i * 0.1) }} />
                                                    <span className="ds-time-legend-label">{item.category}</span>
                                                    <span className="ds-time-legend-pct">{item.displayPct}%</span>
                                                </div>
                                            ))}
                                            {otherPct > 1 && (
                                                <div className="ds-time-legend-item">
                                                    <span className="ds-time-legend-dot" style={{ background: '#D1D1D6' }} />
                                                    <span className="ds-time-legend-label" style={{ fontStyle: 'italic' }}>Other</span>
                                                    <span className="ds-time-legend-pct">{otherPct}%</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            {/* Top Automation Opportunities */}
            {trendsAll.top_automation_opportunities?.length > 0 && (
                <div className="ds-card" style={{ marginBottom: 16 }}>
                    <div className="ds-card-header">
                        <div className="ds-card-header-left">
                            <Zap className="ds-card-icon" size={16} />
                            <span className="ds-card-title">Top Automation Opportunities</span>
                        </div>
                    </div>
                    <div className="ds-card-body">
                        {trendsAll.top_automation_opportunities.map((opp, i) => (
                            <div className="ds-finding" key={i}>
                                <div className="ds-finding-severity high" />
                                <div className="ds-finding-content">
                                    <div className="ds-finding-title">{opp.task}</div>
                                    {opp.potential_solution && <div className="ds-finding-desc">{opp.potential_solution}</div>}
                                    <div className="ds-finding-meta">
                                        {opp.count} mention{opp.count !== 1 ? 's' : ''} across {opp.departments?.join(', ')}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Cross-Department Redundancies */}
            {allRedundancies.length > 0 && (
                <div className="ds-card" style={{ marginBottom: 16 }}>
                    <div className="ds-card-header">
                        <div className="ds-card-header-left">
                            <Repeat2 className="ds-card-icon" size={16} />
                            <span className="ds-card-title">Cross-Department Redundancies</span>
                        </div>
                    </div>
                    <div className="ds-card-body">
                        <table className="ds-table">
                            <thead><tr><th>Task</th><th>Department</th><th>Employees</th><th>Combined Time</th></tr></thead>
                            <tbody>
                                {allRedundancies.slice(0, 15).map((rp, i) => (
                                    <tr key={i}>
                                        <td>{rp.task}</td>
                                        <td>{rp.department}</td>
                                        <td>{rp.employees_involved?.join(', ')}</td>
                                        <td>{rp.combined_time_percentage}%</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Handoff Friction Map */}
            {allHandoffs.length > 0 && (
                <div className="ds-card" style={{ marginBottom: 16 }}>
                    <div className="ds-card-header">
                        <div className="ds-card-header-left">
                            <GitBranch className="ds-card-icon" size={16} />
                            <span className="ds-card-title">Handoff Friction Map</span>
                        </div>
                    </div>
                    <div className="ds-card-body">
                        <table className="ds-table">
                            <thead><tr><th>From</th><th>To</th><th>Frequency</th><th>Pain Points</th></tr></thead>
                            <tbody>
                                {allHandoffs.slice(0, 15).map((hp, i) => (
                                    <tr key={i}>
                                        <td>{hp.from || hp.from_role || hp.source || '-'}</td>
                                        <td>{hp.to || hp.to_role || hp.destination || '-'}</td>
                                        <td>{hp.frequency || hp.handoff_frequency || '-'}</td>
                                        <td>{Array.isArray(hp.pain_points) ? hp.pain_points.join('; ') : Array.isArray(hp.issues) ? hp.issues.join('; ') : hp.pain_points || hp.issues || hp.description || '-'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </>
    );
}

/* ------------------------------------------------------------------ */
/*  TAB 4 — Manage                                                     */
/* ------------------------------------------------------------------ */

function ManageTab({ overview, onRefresh, reportGenerationLock }) {
    const [viewingReport, setViewingReport] = useState(null);
    const [bulkJobId, setBulkJobId] = useState(null);
    const [bulkStatus, setBulkStatus] = useState(null);
    const [bulkBusy, setBulkBusy] = useState(false);
    const [error, setError] = useState(null);
    const pollRef = useRef(null);

    // Report sharing state
    const [selectedSessions, setSelectedSessions] = useState(new Set());
    const [sendingLinks, setSendingLinks] = useState(false);
    const [sentLinks, setSentLinks] = useState([]);
    const [showSentPanel, setShowSentPanel] = useState(false);
    const [invites, setInvites] = useState([]);
    const [loadingInvites, setLoadingInvites] = useState(false);
    const [showInvites, setShowInvites] = useState(false);
    const [copiedId, setCopiedId] = useState(null);
    const [expandedFeedback, setExpandedFeedback] = useState(null);

    const clearPoll = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
    useEffect(() => () => clearPoll(), []);

    const departments = overview?.departments || [];
    const totals = overview?.totals || {};
    const activeJobs = overview?.active_jobs || [];
    const lockedCount = 0; // would need lock info per dept
    const generationLocked = Boolean(reportGenerationLock?.locked);
    const generationLockMessage = reportGenerationLock?.message || 'Report generation is locked.';

    // Gather missing session ids AND sendable sessions (completed with report)
    const missingIds = [];
    const allCompletedIds = [];
    const sendableMap = {}; // session_id → {employee_name, department, session_id}
    departments.forEach(dept => {
        (dept.surveys || []).forEach(s => {
            if (s.status === 'completed' && !s.has_report) missingIds.push(s.session_id);
            if (s.status === 'completed') allCompletedIds.push(s.session_id);
            if (s.status === 'completed' && s.has_report) {
                sendableMap[s.session_id] = { session_id: s.session_id, employee_name: s.employee_name, department: dept.name };
            }
        });
    });
    const sendableCount = Object.keys(sendableMap).length;

    // Toggle selection for a session
    const toggleSelect = (sessionId) => {
        setSelectedSessions(prev => {
            const next = new Set(prev);
            if (next.has(sessionId)) next.delete(sessionId);
            else next.add(sessionId);
            return next;
        });
    };

    // Select / deselect all sendable in a department
    const toggleDeptSelect = (dept) => {
        const deptSendable = (dept.surveys || []).filter(s => s.status === 'completed' && s.has_report);
        const allSelected = deptSendable.every(s => selectedSessions.has(s.session_id));
        setSelectedSessions(prev => {
            const next = new Set(prev);
            deptSendable.forEach(s => {
                if (allSelected) next.delete(s.session_id);
                else next.add(s.session_id);
            });
            return next;
        });
    };

    // Select all sendable across all depts
    const selectAllSendable = () => {
        const allIds = Object.keys(sendableMap);
        const allSelected = allIds.every(id => selectedSessions.has(id));
        if (allSelected) setSelectedSessions(new Set());
        else setSelectedSessions(new Set(allIds));
    };

    // Generate invite links for selected sessions
    const handleSendLinks = async () => {
        if (selectedSessions.size === 0) return;
        setSendingLinks(true);
        setError(null);
        const results = [];
        for (const sessionId of selectedSessions) {
            try {
                const res = await apiFetch(`/report/${sessionId}/invite`, { method: 'POST' });
                results.push({ ...res, status: 'success' });
            } catch (e) {
                const info = sendableMap[sessionId] || {};
                results.push({ employee_name: info.employee_name || 'Unknown', department: info.department || '', status: 'error', error: e.message });
            }
        }
        setSentLinks(results);
        setShowSentPanel(true);
        setSendingLinks(false);
        setSelectedSessions(new Set());
    };

    // Load invite history
    const loadInvites = async () => {
        setLoadingInvites(true);
        try {
            const res = await apiFetch('/report-access/list');
            setInvites(res.tokens || []);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoadingInvites(false);
        }
    };

    // Revoke an invite
    const revokeInvite = async (tokenId) => {
        try {
            await apiFetch(`/report-access/${tokenId}`, { method: 'DELETE' });
            setInvites(prev => prev.map(t => t.id === tokenId ? { ...t, is_revoked: true } : t));
        } catch (e) {
            setError(e.message);
        }
    };

    // Copy link to clipboard
    const copyLink = (url, id) => {
        navigator.clipboard.writeText(url).then(() => {
            setCopiedId(id);
            setTimeout(() => setCopiedId(null), 2000);
        });
    };

    const startBulk = async (sessionIds) => {
        if (bulkBusy) return; // Prevent double-click
        if (generationLocked) {
            setError(generationLockMessage);
            return;
        }
        setBulkBusy(true);
        setError(null);
        clearPoll();
        try {
            const res = await apiFetch('/reports/bulk-regenerate', {
                method: 'POST',
                body: JSON.stringify({ session_ids: sessionIds }),
            });
            if (res.status === 'no_data') { setError('No sessions match the criteria.'); setBulkBusy(false); return; }
            
            const jobId = res.job_id;
            setBulkJobId(jobId);
            
            // For already_running, fetch current status immediately instead of resetting to 0
            if (res.status === 'already_running') {
                try {
                    const current = await apiFetch(`/reports/bulk-regenerate/status/${jobId}`);
                    setBulkStatus(current);
                } catch {
                    setBulkStatus({ status: 'running', completed_count: 0, total: res.total || sessionIds.length });
                }
            } else {
                setBulkStatus({ status: 'running', completed_count: 0, total: res.total || sessionIds.length });
            }
            
            pollRef.current = setInterval(async () => {
                try {
                    const poll = await apiFetch(`/reports/bulk-regenerate/status/${jobId}`);
                    setBulkStatus(poll);
                    if (['completed', 'partial', 'failed'].includes(poll.status)) {
                        clearPoll();
                        setBulkBusy(false);
                        onRefresh();
                    }
                } catch {
                    clearPoll();
                    setBulkBusy(false);
                }
            }, 3000);
        } catch (e) {
            setError(e.message);
            setBulkBusy(false);
        }
    };

    const handleRegenerateSingle = (session) => {
        if (!session?.session_id) return;
        if (!window.confirm(`Regenerate report for ${session.employee_name}?\nThe existing report will be replaced.`)) return;
        startBulk([session.session_id]);
    };

    // Poll active jobs on mount
    useEffect(() => {
        if (activeJobs.length > 0) {
            const job = activeJobs[0];
            setBulkJobId(job.job_id);
            setBulkStatus({ status: job.status, completed_count: job.completed_count, total: job.total });
            if (job.status === 'running' || job.status === 'pending') {
                setBulkBusy(true);
                pollRef.current = setInterval(async () => {
                    try {
                        const poll = await apiFetch(`/reports/bulk-regenerate/status/${job.job_id}`);
                        setBulkStatus(poll);
                        if (['completed', 'partial', 'failed'].includes(poll.status)) {
                            clearPoll();
                            setBulkBusy(false);
                            onRefresh();
                        }
                    } catch {
                        clearPoll();
                        setBulkBusy(false);
                    }
                }, 3000);
            }
        }
    }, []); // eslint-disable-line

    if (viewingReport) {
        return (
            <div>
                <button className="ds-btn ds-btn-secondary" onClick={() => setViewingReport(null)} style={{ marginBottom: 12 }}>
                    <ChevronRight size={14} style={{ transform: 'rotate(180deg)' }} /> Back to Manage
                </button>
                <ReportViewer sessionId={viewingReport} onClose={() => setViewingReport(null)} />
            </div>
        );
    }

    return (
        <>
            {/* Sent Links Panel (overlay) */}
            {showSentPanel && sentLinks.length > 0 && (
                <div className="ds-card" style={{ marginBottom: 16, border: '2px solid var(--color-success)', borderRadius: 12 }}>
                    <div className="ds-card-header" style={{ background: 'var(--color-success-light)' }}>
                        <div className="ds-card-header-left">
                            <CheckCircle2 className="ds-card-icon" size={16} style={{ color: 'var(--color-success)' }} />
                            <span className="ds-card-title">Report Links Generated</span>
                        </div>
                        <button className="ds-action-btn" onClick={() => setShowSentPanel(false)} title="Close">
                            <X size={14} />
                        </button>
                    </div>
                    <div className="ds-card-body">
                        <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', marginBottom: 12 }}>
                            Links expire in 30 days. Emails are sent automatically when an address is on file.
                        </p>
                        <table className="ds-table">
                            <thead>
                                <tr>
                                    <th>Employee</th>
                                    <th>Department</th>
                                    <th>Link</th>
                                    <th>Email</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sentLinks.map((link, i) => (
                                    <tr key={i}>
                                        <td>{link.employee_name}</td>
                                        <td>{link.department}</td>
                                        <td>
                                            {link.status === 'success' ? (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                                    <input 
                                                        type="text" 
                                                        readOnly 
                                                        value={link.access_url} 
                                                        style={{ flex: 1, fontSize: 'var(--font-size-xs)', padding: '4px 8px', border: '1px solid var(--color-border)', borderRadius: 6, background: 'var(--color-bg-tertiary)' }}
                                                        onClick={e => e.target.select()}
                                                    />
                                                    <button 
                                                        className="ds-action-btn" 
                                                        title="Copy link"
                                                        onClick={() => copyLink(link.access_url, `sent-${i}`)}
                                                    >
                                                        {copiedId === `sent-${i}` ? <Check size={12} style={{ color: 'var(--color-success)' }} /> : <Copy size={12} />}
                                                    </button>
                                                </div>
                                            ) : (
                                                <span style={{ color: 'var(--color-error)', fontSize: 'var(--font-size-xs)' }}>—</span>
                                            )}
                                        </td>
                                        <td>
                                            {link.status === 'success' 
                                                ? (link.email_sent 
                                                    ? <span className="ds-badge ds-badge-success"><span className="ds-badge-dot" />Emailed</span>
                                                    : <span className="ds-badge ds-badge-warning" title={link.email_message}><span className="ds-badge-dot" />{link.email_message || 'No email'}</span>
                                                  )
                                                : <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-xs)' }}>—</span>
                                            }
                                        </td>
                                        <td>
                                            {link.status === 'success' 
                                                ? <span className="ds-badge ds-badge-success"><span className="ds-badge-dot" />Sent</span>
                                                : <span className="ds-badge ds-badge-error" title={link.error}><span className="ds-badge-dot" />Failed</span>
                                            }
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Invite History Panel */}
            {showInvites && (
                <div className="ds-card" style={{ marginBottom: 16 }}>
                    <div className="ds-card-header">
                        <div className="ds-card-header-left">
                            <ExternalLink className="ds-card-icon" size={16} />
                            <span className="ds-card-title">Sent Report Links ({invites.length})</span>
                        </div>
                        <button className="ds-action-btn" onClick={() => setShowInvites(false)} title="Close">
                            <X size={14} />
                        </button>
                    </div>
                    <div className="ds-card-body">
                        {loadingInvites ? (
                            <div style={{ textAlign: 'center', padding: 24 }}><Loader2 size={20} className="ds-spinner" /></div>
                        ) : invites.length === 0 ? (
                            <p style={{ textAlign: 'center', color: 'var(--color-text-tertiary)', padding: 24 }}>No report links have been sent yet.</p>
                        ) : (
                            <table className="ds-table">
                                <thead>
                                    <tr>
                                        <th>Employee</th>
                                        <th>Department</th>
                                        <th>Sent</th>
                                        <th>Expires</th>
                                        <th>Views</th>
                                        <th>Status</th>
                                        <th>Feedback</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {invites.map(inv => {
                                        const expired = inv.expires_at && new Date(inv.expires_at) < new Date();
                                        const hasFeedback = !!inv.validated_at;
                                        const isExpanded = expandedFeedback === inv.id;
                                        return (
                                            <React.Fragment key={inv.id}>
                                            <tr style={{ opacity: inv.is_revoked ? 0.5 : 1 }}>
                                                <td>{inv.employee_name}</td>
                                                <td>{inv.department}</td>
                                                <td style={{ fontSize: 'var(--font-size-xs)' }}>{inv.created_at ? new Date(inv.created_at).toLocaleDateString() : '—'}</td>
                                                <td style={{ fontSize: 'var(--font-size-xs)' }}>{inv.expires_at ? new Date(inv.expires_at).toLocaleDateString() : '—'}</td>
                                                <td>{inv.access_count || 0}</td>
                                                <td>
                                                    {inv.is_revoked 
                                                        ? <span className="ds-badge ds-badge-error"><span className="ds-badge-dot" />Revoked</span>
                                                        : expired
                                                            ? <span className="ds-badge ds-badge-warning"><span className="ds-badge-dot" />Expired</span>
                                                            : inv.access_count > 0
                                                                ? <span className="ds-badge ds-badge-success"><span className="ds-badge-dot" />Viewed ({inv.access_count})</span>
                                                                : <span className="ds-badge ds-badge-neutral"><span className="ds-badge-dot" />Pending</span>
                                                    }
                                                </td>
                                                <td>
                                                    {hasFeedback ? (
                                                        <button
                                                            className="ds-action-btn"
                                                            onClick={() => setExpandedFeedback(isExpanded ? null : inv.id)}
                                                            title="View employee feedback"
                                                            style={{ display: 'flex', alignItems: 'center', gap: 4 }}
                                                        >
                                                            <MessageSquare size={12} style={{ color: 'var(--color-success)' }} />
                                                            <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-success)' }}>Received</span>
                                                            {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                                                        </button>
                                                    ) : (
                                                        <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>—</span>
                                                    )}
                                                </td>
                                                <td>
                                                    <div className="ds-action-row">
                                                        {inv.token && !inv.is_revoked && !expired && (
                                                            <a
                                                                href={`/employee/report/${inv.token}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="ds-action-btn"
                                                                title="View report as employee"
                                                            >
                                                                <Eye size={12} />
                                                            </a>
                                                        )}
                                                        {inv.token && !inv.is_revoked && !expired && (
                                                            <button className="ds-action-btn" title="Copy report link" onClick={() => copyLink(`${window.location.origin}/employee/report/${inv.token}`, inv.id)}>
                                                                {copiedId === inv.id ? <Check size={12} style={{ color: 'var(--color-success)' }} /> : <Copy size={12} />}
                                                            </button>
                                                        )}
                                                        {!inv.is_revoked && !expired && (
                                                            <button className="ds-action-btn" title="Revoke access" onClick={() => revokeInvite(inv.id)}>
                                                                <XCircle size={12} style={{ color: 'var(--color-error)' }} />
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                            {isExpanded && hasFeedback && inv.validation_data && (
                                                <tr>
                                                    <td colSpan={8} style={{ padding: 0, background: 'var(--color-bg-secondary)' }}>
                                                        <div style={{ padding: '12px 24px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                                            <div>
                                                                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)', marginBottom: 2 }}>Time Accuracy</div>
                                                                <div style={{ fontSize: 'var(--font-size-sm)' }}>{inv.validation_data.time_accuracy || '—'}</div>
                                                            </div>
                                                            <div>
                                                                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)', marginBottom: 2 }}>Over/Understated</div>
                                                                <div style={{ fontSize: 'var(--font-size-sm)' }}>{inv.validation_data.overstated_understated || '—'}</div>
                                                            </div>
                                                            <div style={{ gridColumn: '1 / -1' }}>
                                                                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)', marginBottom: 2 }}>Missing Responsibilities</div>
                                                                <div style={{ fontSize: 'var(--font-size-sm)' }}>{inv.validation_data.missing_responsibilities || '—'}</div>
                                                            </div>
                                                            <div style={{ gridColumn: '1 / -1' }}>
                                                                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)', marginBottom: 2 }}>Additional Feedback</div>
                                                                <div style={{ fontSize: 'var(--font-size-sm)' }}>{inv.validation_data.additional_feedback || '—'}</div>
                                                            </div>
                                                            <div style={{ gridColumn: '1 / -1', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>
                                                                Submitted {new Date(inv.validated_at).toLocaleDateString()} at {new Date(inv.validated_at).toLocaleTimeString()}
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            )}

            {/* Stat cards */}
            <div className="ds-stats-grid">
                <div className="ds-stat-card">
                    <div className="ds-stat-header"><span className="ds-stat-label">Missing Reports</span><span className="ds-stat-icon orange"><FileText size={16} /></span></div>
                    <div className="ds-stat-value">{totals.reports_missing ?? missingIds.length}</div>
                </div>
                <div className="ds-stat-card">
                    <div className="ds-stat-header"><span className="ds-stat-label">Reports Generated</span><span className="ds-stat-icon green"><CheckCircle2 size={16} /></span></div>
                    <div className="ds-stat-value">{totals.reports_generated ?? 0}</div>
                </div>
                <div className="ds-stat-card">
                    <div className="ds-stat-header"><span className="ds-stat-label">Active Jobs</span><span className="ds-stat-icon purple"><Loader2 size={16} /></span></div>
                    <div className="ds-stat-value">{activeJobs.length}</div>
                </div>
                <div className="ds-stat-card">
                    <div className="ds-stat-header"><span className="ds-stat-label">Total Surveys</span><span className="ds-stat-icon blue"><Users size={16} /></span></div>
                    <div className="ds-stat-value">{totals.total_surveys ?? 0}</div>
                </div>
            </div>

            {/* Bulk actions + Send controls */}
            <div className="ds-action-row" style={{ marginTop: 16, marginBottom: 8, flexWrap: 'wrap' }}>
                <button className="ds-btn ds-btn-primary" disabled={generationLocked || bulkBusy || missingIds.length === 0} onClick={() => startBulk(missingIds)}>
                    {bulkBusy ? <Loader2 size={14} className="ds-spinner" /> : <Zap size={14} />}
                    Generate All Missing ({missingIds.length})
                </button>
                <button className="ds-btn ds-btn-secondary" disabled={generationLocked || bulkBusy || allCompletedIds.length === 0} onClick={() => startBulk(allCompletedIds)}>
                    {bulkBusy ? <Loader2 size={14} className="ds-spinner" /> : <RefreshCw size={14} />}
                    Regenerate All ({allCompletedIds.length})
                </button>
                <div style={{ flex: 1 }} />
                <button 
                    className="ds-btn ds-btn-secondary" 
                    onClick={() => { setShowInvites(!showInvites); if (!showInvites) loadInvites(); }}
                >
                    <ExternalLink size={14} /> {showInvites ? 'Hide' : 'View'} Sent Links
                </button>
            </div>

            {/* Send selected bar */}
            {selectedSessions.size > 0 && (
                <div className="ds-card" style={{ marginBottom: 12, border: '2px solid var(--color-primary)', borderRadius: 12 }}>
                    <div className="ds-card-body" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px' }}>
                        <CheckCircle2 size={16} style={{ color: 'var(--color-primary)' }} />
                        <span style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>
                            {selectedSessions.size} employee{selectedSessions.size !== 1 ? 's' : ''} selected
                        </span>
                        <div style={{ flex: 1 }} />
                        <button className="ds-btn ds-btn-secondary" onClick={() => setSelectedSessions(new Set())} style={{ fontSize: 'var(--font-size-xs)' }}>
                            Clear
                        </button>
                        <button 
                            className="ds-btn ds-btn-primary" 
                            onClick={handleSendLinks}
                            disabled={sendingLinks}
                        >
                            {sendingLinks ? <Loader2 size={14} className="ds-spinner" /> : <Send size={14} />}
                            Generate Report Links ({selectedSessions.size})
                        </button>
                    </div>
                </div>
            )}

            <GenerationLockedNotice status={reportGenerationLock} />
            {error && <div className="ds-badge ds-badge-error" style={{ display: 'inline-flex', marginBottom: 12 }}><AlertTriangle size={14} /> {error}</div>}

            {bulkStatus && bulkBusy && (
                <div className="ds-card" style={{ marginBottom: 16 }}>
                    <div className="ds-card-body">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                            <Loader2 size={14} className="ds-spinner" />
                            <span style={{ flex: 1 }}>Bulk generation in progress: {bulkStatus.completed_count ?? 0} / {bulkStatus.total}{bulkStatus.current_item ? ` — ${bulkStatus.current_item}` : ''}</span>
                            <button className="ds-action-btn" title="Dismiss stuck progress indicator" onClick={() => { clearPoll(); setBulkBusy(false); setBulkStatus(null); }}>
                                <X size={14} /> Dismiss
                            </button>
                        </div>
                        <div className="ds-progress">
                            <div className="ds-progress-fill green" style={{ width: `${bulkStatus.total > 0 ? Math.round(((bulkStatus.completed_count ?? 0) / bulkStatus.total) * 100) : 0}%` }} />
                        </div>
                    </div>
                </div>
            )}

            {/* Department table */}
            <div className="ds-card">
                <div className="ds-card-header">
                    <div className="ds-card-header-left">
                        <Settings2 className="ds-card-icon" size={16} />
                        <span className="ds-card-title">Department Reports</span>
                    </div>
                    {sendableCount > 0 && (
                        <button className="ds-action-btn" title={`Select all ${sendableCount} employees with reports`} onClick={selectAllSendable}
                            style={{ fontSize: 'var(--font-size-xs)', display: 'flex', alignItems: 'center', gap: 4 }}>
                            <Send size={12} /> Select All ({sendableCount})
                        </button>
                    )}
                </div>
                <div className="ds-card-body">
                    {departments.length === 0 ? (
                        <EmptyState icon={FileText} title="No departments" desc="Reports will appear once surveys are completed." />
                    ) : (
                        <table className="ds-table">
                            <thead>
                                <tr>
                                    <th style={{ width: 32 }}></th>
                                    <th>Department</th>
                                    <th>Status</th>
                                    <th>Reports</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {departments.map(dept => {
                                    const deptSendable = (dept.surveys || []).filter(s => s.status === 'completed' && s.has_report);
                                    const allDeptSelected = deptSendable.length > 0 && deptSendable.every(s => selectedSessions.has(s.session_id));
                                    const someDeptSelected = deptSendable.some(s => selectedSessions.has(s.session_id));
                                    return (
                                    <React.Fragment key={dept.name}>
                                        <tr>
                                            <td>
                                                {deptSendable.length > 0 && (
                                                    <input 
                                                        type="checkbox" 
                                                        checked={allDeptSelected}
                                                        ref={el => { if (el) el.indeterminate = someDeptSelected && !allDeptSelected; }}
                                                        onChange={() => toggleDeptSelect(dept)}
                                                        title={`Select all in ${dept.name}`}
                                                        style={{ cursor: 'pointer', width: 16, height: 16, accentColor: 'var(--color-primary)' }}
                                                    />
                                                )}
                                            </td>
                                            <td style={{ fontWeight: 600 }}>{dept.name}</td>
                                            <td><StatusBadge completed={dept.completed} total={dept.total} /></td>
                                            <td>{dept.reports_generated} / {dept.completed}</td>
                                            <td>
                                                <div className="ds-action-row">
                                                    {dept.surveys?.filter(s => s.has_report).slice(0, 1).map(s => (
                                                        <button key={s.session_id} className="ds-action-btn" title="View first report" onClick={() => setViewingReport(s.session_id)}>
                                                            <Eye size={14} />
                                                        </button>
                                                    ))}
                                                    <button className="ds-action-btn" title="Regenerate department reports"
                                                        disabled={generationLocked || bulkBusy || dept.completed === 0}
                                                        onClick={() => {
                                                            const ids = dept.surveys?.filter(s => s.status === 'completed').map(s => s.session_id) || [];
                                                            if (ids.length > 0) startBulk(ids);
                                                        }}>
                                                        <RefreshCw size={14} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                        {/* Inline employee rows */}
                                        {dept.surveys?.map(s => {
                                            const canSend = s.status === 'completed' && s.has_report;
                                            return (
                                            <tr key={s.session_id} style={{ fontSize: 'var(--font-size-xs)' }}>
                                                <td>
                                                    {canSend && (
                                                        <input 
                                                            type="checkbox"
                                                            checked={selectedSessions.has(s.session_id)}
                                                            onChange={() => toggleSelect(s.session_id)}
                                                            style={{ cursor: 'pointer', width: 14, height: 14, accentColor: 'var(--color-primary)' }}
                                                        />
                                                    )}
                                                </td>
                                                <td style={{ paddingLeft: 24 }}>{s.employee_name}</td>
                                                <td>
                                                    <span className={`ds-badge ${s.status === 'completed' ? 'ds-badge-success' : s.status === 'in_progress' ? 'ds-badge-warning' : 'ds-badge-neutral'}`}>
                                                        <span className="ds-badge-dot" />{s.status}
                                                    </span>
                                                </td>
                                                <td>{s.has_report ? <CheckCircle2 size={12} style={{ color: 'var(--color-success)' }} /> : s.status === 'completed' ? <span style={{ color: 'var(--color-warning)' }}>Missing</span> : '-'}</td>
                                                <td>
                                                    <div className="ds-action-row">
                                                        {s.has_report && (
                                                            <button className="ds-action-btn" title="View report" onClick={() => setViewingReport(s.session_id)}>
                                                                <Eye size={12} />
                                                            </button>
                                                        )}
                                                        {s.has_report && (
                                                            <button
                                                                className="ds-action-btn"
                                                                title="Regenerate report"
                                                                disabled={generationLocked || bulkBusy}
                                                                onClick={() => handleRegenerateSingle(s)}
                                                            >
                                                                <RefreshCw size={12} />
                                                            </button>
                                                        )}
                                                        {canSend && (
                                                            <button 
                                                                className="ds-action-btn" 
                                                                title="Generate report link for this employee"
                                                                onClick={async () => {
                                                                    try {
                                                                        const res = await apiFetch(`/report/${s.session_id}/invite`, { method: 'POST' });
                                                                        setSentLinks([{ ...res, status: 'success' }]);
                                                                        setShowSentPanel(true);
                                                                    } catch (e) {
                                                                        setError(e.message);
                                                                    }
                                                                }}
                                                            >
                                                                <Send size={12} />
                                                            </button>
                                                        )}
                                                        <button 
                                                            className="ds-action-btn" 
                                                            title="Delete session and report"
                                                            style={{ color: 'var(--color-danger, #e53e3e)' }}
                                                            onClick={async () => {
                                                                if (!window.confirm(`Delete ${s.employee_name}'s survey session and report? This cannot be undone.`)) return;
                                                                try {
                                                                    await apiFetch(`/sessions/${s.session_id}`, { method: 'DELETE' });
                                                                    onRefresh();
                                                                } catch (e) {
                                                                    setError(e.message);
                                                                }
                                                            }}
                                                        >
                                                            <Trash2 size={12} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                            );
                                        })}
                                    </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </>
    );
}

/* ------------------------------------------------------------------ */
/*  Main Hub Component                                                 */
/* ------------------------------------------------------------------ */

export default function ReportsHub({ reportGenerationLock }) {
    const [activeTab, setActiveTab] = useState('org');

    // Shared data
    const [stats, setStats] = useState(null);
    const [overview, setOverview] = useState(null);
    const [trendsAll, setTrendsAll] = useState(null);
    const [deptTrendsMap, setDeptTrendsMap] = useState({});
    const [departments, setDepartments] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchCoreData = useCallback(async () => {
        setLoading(true);
        try {
            const [statsRes, overviewRes] = await Promise.all([
                apiFetch('/stats'),
                apiFetch('/report-management/overview'),
            ]);
            setStats(statsRes);
            setOverview(overviewRes);
            const deptNames = (overviewRes.departments || []).map(d => d.name).filter(Boolean);
            setDepartments(deptNames);
        } catch (e) {
            console.error('Failed to load core data:', e);
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchTrendsAll = useCallback(async () => {
        try {
            const data = await apiFetch('/trends/all');
            setTrendsAll(data);
        } catch (e) {
            console.error('Failed to load trends:', e);
        }
    }, []);

    const fetchDeptTrends = useCallback(async (dept) => {
        try {
            const data = await apiFetch(`/trends/department/${encodeURIComponent(dept)}`);
            setDeptTrendsMap(prev => ({ ...prev, [dept]: data }));
        } catch (e) {
            console.error(`Failed to load trends for ${dept}:`, e);
        }
    }, []);

    useEffect(() => {
        fetchCoreData();
        fetchTrendsAll();
    }, [fetchCoreData, fetchTrendsAll]);

    const [pendingDept, setPendingDept] = useState(null);

    const handleSelectDept = (deptName) => {
        setPendingDept(deptName);
        setActiveTab('dept');
    };

    if (loading) return <Spinner text="Loading reports..." />;

    const tabItems = [
        { id: 'org', label: 'Organization', icon: Building2 },
        { id: 'dept', label: 'Departments', icon: BarChart3 },
        { id: 'trends', label: 'Trends', icon: TrendingUp },
        { id: 'admin', label: 'Manage', icon: Settings2 },
    ];

    return (
        <div>
            <div className="ds-page-header">
                <div>
                    <h1 className="ds-page-title">Reports</h1>
                    <p className="ds-page-subtitle">AI-generated analysis and department insights</p>
                </div>
            </div>
            <div className="ds-tab-nav">
                {tabItems.map(tab => (
                    <button
                        key={tab.id}
                        className={`ds-tab-item ${activeTab === tab.id ? 'active' : ''}`}
                        onClick={() => setActiveTab(tab.id)}
                    >
                        <tab.icon size={14} />
                        {tab.label}
                    </button>
                ))}
            </div>
            <div className="ds-page-body">
                {activeTab === 'org' && <OrgTab stats={stats} overview={overview} onSelectDept={handleSelectDept} />}
                {activeTab === 'dept' && <DeptTab departments={departments} initialDept={pendingDept} onDeptUsed={() => setPendingDept(null)} reportGenerationLock={reportGenerationLock} />}
                {activeTab === 'trends' && <TrendsTab trendsAll={trendsAll} deptTrendsMap={deptTrendsMap} departments={departments} fetchDeptTrends={fetchDeptTrends} />}
                {activeTab === 'admin' && <ManageTab overview={overview} onRefresh={fetchCoreData} reportGenerationLock={reportGenerationLock} />}
            </div>
        </div>
    );
}
